import { useState } from "react";
import { Check, X, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Database } from "@/integrations/supabase/types";

type DocumentType = Database["public"]["Enums"]["document_type"];
type DocumentCategory = Database["public"]["Enums"]["document_category"];

interface ReviewValidationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  currentType: DocumentType;
  currentCategory: DocumentCategory | null;
  suggestedType?: string;
  confidenceScore?: number | null;
  onValidated: () => void;
}

const TYPE_OPTIONS: { value: DocumentType; label: string; category: DocumentCategory }[] = [
  // Identité
  { value: "identity", label: "Pièce d'identité", category: "administrative" },
  { value: "certificate", label: "Certificat / Acte", category: "administrative" },
  // Domicile
  { value: "invoice", label: "Facture", category: "housing" },
  { value: "receipt", label: "Quittance / Reçu", category: "housing" },
  { value: "contract", label: "Contrat / Bail", category: "housing" },
  { value: "notarial_deed", label: "Acte notarié", category: "housing" },
  // Banque & Fiscalité
  { value: "payslip", label: "Bulletin de paie", category: "banking" },
  { value: "tax", label: "Document fiscal", category: "banking" },
  { value: "bank_statement", label: "Relevé bancaire / RIB", category: "banking" },
  // Professionnel
  { value: "cv", label: "CV / Curriculum Vitae", category: "work" },
  { value: "attestation", label: "Attestation", category: "work" },
  { value: "kbis", label: "Extrait KBIS", category: "work" },
  { value: "statement", label: "Bilan / Relevé de notes", category: "accounting" },
  // Santé
  { value: "medical_certificate", label: "Certificat médical", category: "health" },
  { value: "prescription", label: "Ordonnance", category: "health" },
  // Véhicule
  { value: "gray_card", label: "Carte grise", category: "vehicle" },
  { value: "insurance", label: "Assurance", category: "insurance" },
  // Divers
  { value: "warranty", label: "Garantie", category: "other" },
  { value: "quote", label: "Devis", category: "other" },
  { value: "administrative", label: "Document administratif", category: "administrative" },
  { value: "other", label: "Autre", category: "other" },
];

export function ReviewValidationDialog({
  open,
  onOpenChange,
  documentId,
  currentType,
  currentCategory,
  suggestedType,
  confidenceScore,
  onValidated,
}: ReviewValidationDialogProps) {
  const [selectedType, setSelectedType] = useState<DocumentType>(currentType);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleValidate = async (keepCurrent: boolean) => {
    setIsSubmitting(true);
    
    try {
      const typeToUse = keepCurrent ? currentType : selectedType;
      const typeOption = TYPE_OPTIONS.find(t => t.value === typeToUse);
      
      const { error } = await supabase
        .from("documents")
        .update({
          doc_type: typeToUse,
          category: typeOption?.category || currentCategory,
          status: "complete",
          classification_method: "manual",
        })
        .eq("id", documentId);

      if (error) throw error;

      // Log the correction for AI training
      if (!keepCurrent) {
        await supabase.from("document_corrections").insert({
          document_id: documentId,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          field_name: "doc_type",
          original_value: currentType,
          corrected_value: typeToUse,
        });
      }

      toast.success("Document validé");
      onValidated();
      onOpenChange(false);
    } catch (error) {
      console.error("Validation error:", error);
      toast.error("Erreur lors de la validation");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Vérification du document</DialogTitle>
          <DialogDescription>
            La classification automatique a une confiance de{" "}
            <span className="font-semibold text-orange-600">
              {confidenceScore ? Math.round(confidenceScore) : 0}%
            </span>
            . Veuillez vérifier et corriger si nécessaire.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Type de document suggéré</Label>
            <div className="p-3 bg-muted rounded-lg">
              <span className="font-medium">
                {TYPE_OPTIONS.find(t => t.value === currentType)?.label || currentType}
              </span>
              {suggestedType && suggestedType !== currentType && (
                <span className="text-sm text-muted-foreground ml-2">
                  (suggestion: {suggestedType})
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Corriger le type</Label>
            <Select value={selectedType} onValueChange={(v) => setSelectedType(v as DocumentType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => handleValidate(true)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Garder ce type
              </>
            )}
          </Button>
          <Button
            className="flex-1"
            onClick={() => handleValidate(false)}
            disabled={isSubmitting || selectedType === currentType}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Appliquer correction
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
